import moment from 'moment';

/** IANA zone for office / API locality (AU / IN). */
export function getIanaTimezoneForLocality(locality = []) {
  const codes = Array.isArray(locality) ? locality : [locality];
  if (codes.some((c) => String(c).toUpperCase() === 'AU')) {
    return 'Australia/Sydney';
  }
  return 'Asia/Kolkata';
}

/** Offset in minutes for IANA timezone (e.g. Asia/Kolkata → 330). */
export function getTimezoneOffsetMinutes(timezone, referenceDate = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset'
    }).formatToParts(referenceDate);
    const tzPart = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT';
    const match = tzPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return 0;

    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number(match[2]) || 0;
    const minutes = Number(match[3]) || 0;
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
}

/**
 * Build API `from` / `to` for a calendar day, week, or month in the given timezone.
 * Uses literal picker year/month/date — not browser UTC conversion.
 */
export function buildTimedoctorRange(value, timezone, rangeType = 'Day') {
  const pickerMoment = moment.isMoment(value) ? value : moment(value);
  const offsetMinutes = getTimezoneOffsetMinutes(
    timezone,
    new Date(pickerMoment.year(), pickerMoment.month(), pickerMoment.date(), 12, 0, 0)
  );

  const anchor = moment({
    year: pickerMoment.year(),
    month: pickerMoment.month(),
    date: pickerMoment.date()
  }).utcOffset(offsetMinutes, true);

  let start;
  let end;
  if (rangeType === 'Week') {
    start = anchor.clone().startOf('week');
    end = anchor.clone().endOf('week');
  } else if (rangeType === 'Month') {
    start = anchor.clone().startOf('month');
    end = anchor.clone().endOf('month');
  } else {
    start = anchor.clone().startOf('day');
    end = anchor.clone().endOf('day');
  }

  return {
    from: start.toISOString(),
    to: end.toISOString()
  };
}

/** Calendar date (YYYY-MM-DD) for a timestamp in the given timezone. */
export function toLocalDateKey(iso, timezone) {
  if (!iso && iso !== 0) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(iso));
  } catch {
    return moment(iso).format('YYYY-MM-DD');
  }
}

/** HH:mm clock in the given timezone (for time inputs). */
export function formatClockInTimezone(ts, timezone) {
  if (!ts && ts !== 0) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date(ts));
    const hour = parts.find((p) => p.type === 'hour')?.value || '00';
    const minute = parts.find((p) => p.type === 'minute')?.value || '00';
    return `${hour}:${minute}`;
  } catch {
    const date = new Date(ts);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
}

/**
 * Build UTC ms for a wall-clock HH:mm on the same calendar day as `anchorTs`
 * in the given timezone.
 */
export function wallTimeOnAnchorDayToUtcMs(hhmm, anchorTs, timezone) {
  if (!hhmm || !Number.isFinite(Number(anchorTs))) return null;
  const [hours, minutes] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const dateKey = toLocalDateKey(anchorTs, timezone);
  if (!dateKey) return null;
  const [y, mo, d] = dateKey.split('-').map(Number);
  const offsetMinutes = getTimezoneOffsetMinutes(
    timezone,
    new Date(Date.UTC(y, mo - 1, d, 12, 0, 0))
  );

  return moment({
    year: y,
    month: mo - 1,
    date: d,
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0
  })
    .utcOffset(offsetMinutes, true)
    .valueOf();
}

function buildDayFromSessions(date, sessions = [], breaks = []) {
  const sortedSessions = [...sessions].sort(
    (a, b) =>
      new Date(a.startTime || a.start).getTime() - new Date(b.startTime || b.start).getTime()
  );
  const sortedBreaks = [...breaks].sort(
    (a, b) =>
      new Date(a.startTime || a.start).getTime() - new Date(b.startTime || b.start).getTime()
  );

  const totalTrackedSeconds = sortedSessions.reduce(
    (sum, session) => sum + (Number(session.durationSeconds) || 0),
    0
  );
  const totalBreakSeconds = sortedBreaks.reduce(
    (sum, item) => sum + (Number(item.durationSeconds) || 0),
    0
  );

  return {
    date,
    startTime: sortedSessions[0]?.startTime || sortedSessions[0]?.start || null,
    endTime:
      sortedSessions[sortedSessions.length - 1]?.endTime ||
      sortedSessions[sortedSessions.length - 1]?.end ||
      null,
    totalTrackedSeconds,
    totalBreakSeconds,
    totalTrackedFormatted: formatSecondsToDuration(totalTrackedSeconds),
    totalBreakFormatted: formatSecondsToDuration(totalBreakSeconds),
    sessions: sortedSessions,
    breaks: sortedBreaks
  };
}

/**
 * Split a day payload whose sessions span multiple calendar days into one object per date.
 * Handles week responses that arrive as a single merged object.
 */
export function splitActivityDayByDate(day, timezone) {
  if (!day || typeof day !== 'object') return [];

  const sessions = Array.isArray(day.sessions) ? day.sessions : [];
  const breaks = Array.isArray(day.breaks) ? day.breaks : [];

  if (!sessions.length && !breaks.length) {
    return day.date || day.startTime ? [day] : [];
  }

  const dateKeys = new Set();
  sessions.forEach((session) => {
    const key = toLocalDateKey(session?.startTime || session?.start, timezone);
    if (key) dateKeys.add(key);
  });

  // Already a single calendar day — keep as-is
  if (dateKeys.size <= 1) {
    return [day];
  }

  return Array.from(dateKeys)
    .sort()
    .map((dateKey) => {
      const daySessions = sessions.filter(
        (session) => toLocalDateKey(session?.startTime || session?.start, timezone) === dateKey
      );
      const dayBreaks = breaks.filter((item) => {
        const startKey = toLocalDateKey(item?.startTime || item?.start, timezone);
        const endKey = toLocalDateKey(item?.endTime || item?.end, timezone);
        // Keep same-day breaks only (drop overnight gaps between days)
        return startKey === dateKey && endKey === dateKey;
      });
      return buildDayFromSessions(dateKey, daySessions, dayBreaks);
    });
}

/** Normalize API `data` — single day / multi-day / merged week object → day array. */
export function normalizeActivityWorkLogDays(data, timezone = 'Asia/Calcutta') {
  if (!data) return [];

  const days = Array.isArray(data) ? data : [data];
  return days.flatMap((day) => splitActivityDayByDate(day, timezone));
}

export function formatSecondsToDuration(totalSeconds = 0) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return '0m';
}

export function aggregateDayTotals(days = []) {
  const totalTrackedSeconds = days.reduce(
    (sum, day) => sum + (Number(day?.totalTrackedSeconds) || 0),
    0
  );
  const totalBreakSeconds = days.reduce(
    (sum, day) => sum + (Number(day?.totalBreakSeconds) || 0),
    0
  );

  return {
    totalTrackedSeconds,
    totalBreakSeconds,
    totalTrackedFormatted: formatSecondsToDuration(totalTrackedSeconds),
    totalBreakFormatted: formatSecondsToDuration(totalBreakSeconds)
  };
}

export function formatTimedoctorTime(iso, timezone) {
  if (!iso) return '—';

  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return moment(iso).format('h:mm A');
  }
}

/** Clock with seconds in office TZ — e.g. 12:55:01 PM (HRX Attendance). */
export function formatTimestampInTimezone(ts, timezone, withSeconds = true) {
  if (ts == null || ts === '') return '';
  try {
    return new Date(Number(ts)).toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      ...(withSeconds ? { second: '2-digit' } : {}),
      hour12: true
    });
  } catch {
    return moment(Number(ts)).format(withSeconds ? 'h:mm:ss A' : 'h:mm A');
  }
}

/** datetime-local value (YYYY-MM-DDTHH:mm) in the given timezone. */
export function toDatetimeLocalInTimezone(ts, timezone) {
  if (ts == null || ts === '') return '';
  const dateKey = toLocalDateKey(ts, timezone);
  const clock = formatClockInTimezone(ts, timezone);
  if (!dateKey || !clock) return '';
  return `${dateKey}T${clock}`;
}

/** Parse datetime-local wall time in office TZ → UTC ms. */
export function datetimeLocalInTimezoneToUtcMs(datetimeLocal, timezone) {
  if (!datetimeLocal) return null;
  const [datePart, timePart] = String(datetimeLocal).split('T');
  if (!datePart || !timePart) return null;
  const [y, mo, d] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  if (![y, mo, d, hours, minutes].every(Number.isFinite)) return null;

  const offsetMinutes = getTimezoneOffsetMinutes(
    timezone,
    new Date(Date.UTC(y, mo - 1, d, 12, 0, 0))
  );

  return moment({
    year: y,
    month: mo - 1,
    date: d,
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0
  })
    .utcOffset(offsetMinutes, true)
    .valueOf();
}

export function formatTimedoctorDate(dateStr, timezone) {
  if (!dateStr) return '—';

  try {
    const formatted = new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-GB', {
      timeZone: timezone,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return formatted.replace(/ /g, '-');
  } catch {
    return moment(dateStr).format('DD-MMM-YYYY');
  }
}

export function mapDayToTableRow(day, timezone) {
  return {
    id: day?.date || `day-${day?.startTime || 'empty'}`,
    date: formatTimedoctorDate(day?.date, timezone),
    startTime: formatTimedoctorTime(day?.startTime, timezone),
    endTime: formatTimedoctorTime(day?.endTime, timezone),
    tracked: day?.totalTrackedFormatted || '0m',
    raw: day
  };
}

export function flattenBreaksForDisplay(days = [], timezone) {
  return days.flatMap((day) =>
    (day?.breaks || []).map((breakItem, index) => ({
      id: `${day?.date || 'day'}-break-${index}`,
      date: formatTimedoctorDate(day?.date, timezone),
      startTime: formatTimedoctorTime(breakItem?.startTime, timezone),
      endTime: formatTimedoctorTime(breakItem?.endTime, timezone),
      duration: breakItem?.durationFormatted || '—',
      source: breakItem?.source || '—'
    }))
  );
}

export function flattenSessionsForDisplay(days = [], timezone) {
  return days.flatMap((day) =>
    (day?.sessions || []).map((session, index) => ({
      id: `${day?.date || 'day'}-session-${index}`,
      date: formatTimedoctorDate(day?.date, timezone),
      startTime: formatTimedoctorTime(session?.startTime, timezone),
      endTime: formatTimedoctorTime(session?.endTime, timezone),
      duration: session?.durationFormatted || '—',
      taskName: session?.taskName || '—',
      projectName: session?.projectName || '—'
    }))
  );
}

/** Merge work sessions + breaks into one chronological activity list. */
export function buildActivityTimeline(days = [], timezone) {
  const items = [];

  days.forEach((day) => {
    const dateLabel = formatTimedoctorDate(day?.date, timezone);
    const dateKey = day?.date || 'unknown';

    (day?.sessions || []).forEach((session, index) => {
      items.push({
        id: `${dateKey}-work-${index}-${session?.startTime || index}`,
        type: 'work',
        dateKey,
        date: dateLabel,
        startIso: session?.startTime || session?.start || '',
        endIso: session?.endTime || '',
        startTime: formatTimedoctorTime(session?.startTime || session?.start, timezone),
        endTime: formatTimedoctorTime(session?.endTime, timezone),
        duration: session?.durationFormatted || '—',
        label: session?.taskName || 'Work session',
        meta: session?.projectName || '—'
      });
    });

    (day?.breaks || []).forEach((breakItem, index) => {
      const sourceLabel =
        breakItem?.source === 'timedoctor' ? 'TimeDoctor break' : 'Gap between sessions';

      items.push({
        id: `${dateKey}-break-${index}-${breakItem?.startTime || index}`,
        type: 'break',
        dateKey,
        date: dateLabel,
        startIso: breakItem?.startTime || breakItem?.start || '',
        endIso: breakItem?.endTime || breakItem?.end || '',
        startTime: formatTimedoctorTime(breakItem?.startTime || breakItem?.start, timezone),
        endTime: formatTimedoctorTime(breakItem?.endTime || breakItem?.end, timezone),
        duration: breakItem?.durationFormatted || '—',
        label: 'Break',
        meta: sourceLabel,
        source: breakItem?.source || 'gap'
      });
    });
  });

  return items.sort((a, b) => {
    const aTime = a.startIso ? new Date(a.startIso).getTime() : 0;
    const bTime = b.startIso ? new Date(b.startIso).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return a.type === 'work' ? -1 : 1;
  });
}

export function groupTimelineByDate(items = []) {
  const groups = [];
  const map = new Map();

  items.forEach((item) => {
    if (!map.has(item.dateKey)) {
      const group = { dateKey: item.dateKey, date: item.date, items: [] };
      map.set(item.dateKey, group);
      groups.push(group);
    }
    map.get(item.dateKey).items.push(item);
  });

  return groups;
}

export function hasAttendanceData(days = []) {
  return days.some((day) => day?.startTime && (day?.sessions?.length || 0) > 0);
}

const WORK_GRADIENT = 'linear-gradient(90deg, #a855f7 0%, #6366f1 100%)';

/** Visual timeline segments for a single day row (matches Time Tracking bar). */
export function buildDayTimelineSegments(day, timezone) {
  const spanStartMs = day?.startTime ? new Date(day.startTime).getTime() : null;
  const spanEndMs = day?.endTime ? new Date(day.endTime).getTime() : null;

  if (!Number.isFinite(spanStartMs) || !Number.isFinite(spanEndMs) || spanEndMs <= spanStartMs) {
    return { spanStartMs, spanEndMs, segments: [] };
  }

  const span = spanEndMs - spanStartMs;
  const fmtClock = (iso) => formatTimedoctorTime(iso, timezone);
  const rawSegments = [];

  (day?.sessions || []).forEach((session) => {
    const startMs = new Date(session?.startTime || session?.start).getTime();
    const endMs = new Date(session?.endTime).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;

    const task = session?.taskName || 'Work session';
    rawSegments.push({
      kind: 'work',
      startMs,
      endMs,
      color: WORK_GRADIENT,
      label: `${task} • ${fmtClock(session?.startTime || session?.start)} – ${fmtClock(session?.endTime)}`
    });
  });

  (day?.breaks || []).forEach((breakItem) => {
    const startMs = new Date(breakItem?.startTime || breakItem?.start).getTime();
    const endMs = new Date(breakItem?.endTime || breakItem?.end).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;

    const source = breakItem?.source === 'timedoctor' ? 'TimeDoctor break' : 'Gap between sessions';
    rawSegments.push({
      kind: 'break',
      startMs,
      endMs,
      label: `Break • ${fmtClock(breakItem?.startTime || breakItem?.start)} – ${fmtClock(breakItem?.endTime || breakItem?.end)} (${source})`
    });
  });

  const segments = rawSegments
    .sort((a, b) => a.startMs - b.startMs)
    .map((segment) => {
      const left = (segment.startMs - spanStartMs) / span;
      const width = (segment.endMs - segment.startMs) / span;
      return {
        kind: segment.kind,
        left: Math.max(0, Math.min(1, left)),
        width: Math.max(0, Math.min(1, width)),
        color: segment.color,
        label: segment.label
      };
    })
    .filter((segment) => segment.width > 0);

  return { spanStartMs, spanEndMs, segments };
}

export function isWeekendDate(dateStr) {
  if (!dateStr) return false;
  const m = moment(dateStr, ['YYYY-MM-DD', moment.ISO_8601], true);
  if (!m.isValid()) return false;
  const day = m.day();
  return day === 0 || day === 6;
}
